import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CloseCircle from 'assets/icons/CloseCircle';
import { KEY_BOARD, OVERLAY_Z_INDEX_CLASS, TAB_INDEX } from 'constants/general';
import COLOR from 'constants/color';
import { BaseButtonSm } from './BaseButton';

export type DrawerSide = 'left' | 'right' | 'top' | 'bottom';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  side?: DrawerSide;
  width?: number | string;
  height?: number | string;
  overlayOpacity?: number; // 0..1
  closeOnOverlayClick?: boolean;
  disableScrollLock?: boolean;
  className?: string;
  overlayZIndexClass?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  initialFocusRef?: React.RefObject<HTMLElement>;
  returnFocusOnClose?: boolean;
  closeButton?: React.ReactNode;
  headerActions?: React.ReactNode;
  showCloseButton?: boolean;
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  side = 'right',
  width = 360,
  height = 360,
  overlayOpacity = 0.5,
  closeOnOverlayClick = true,
  disableScrollLock = false,
  className = '',
  overlayZIndexClass = OVERLAY_Z_INDEX_CLASS.DRAWER,
  header,
  footer,
  children,
  ariaLabel,
  ariaLabelledBy,
  initialFocusRef,
  returnFocusOnClose = true,
  closeButton,
  headerActions,
  showCloseButton = true
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (disableScrollLock) {
      return;
    }
    if (isOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isOpen, disableScrollLock]);

  // Manage focus when opening/closing
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElementRef.current =
        (document.activeElement as HTMLElement) || null;
      const toFocus = initialFocusRef?.current || panelRef.current;
      // Defer to next tick to ensure element is mounted
      setTimeout(() => {
        toFocus?.focus();
      }, 0);
    } else if (returnFocusOnClose && previouslyFocusedElementRef.current) {
      previouslyFocusedElementRef.current.focus?.();
      previouslyFocusedElementRef.current = null;
    }
  }, [isOpen, initialFocusRef, returnFocusOnClose]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) {
        return;
      }
      if (e.key === KEY_BOARD.ESCAPE.KEY) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const container = panelRef.current;
        if (!container) {
          return;
        }
        const focusableSelectors = [
          'a[href]',
          'area[href]',
          'button:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          'iframe',
          'object',
          'embed',
          '[tabindex]:not([tabindex="-1"])',
          '[contenteditable="true"]'
        ];
        const focusable = Array.from(
          container.querySelectorAll<HTMLElement>(focusableSelectors.join(','))
        ).filter(
          (el) =>
            !el.hasAttribute('disabled') &&
            el.tabIndex !== -1 &&
            !el.getAttribute('aria-hidden')
        );
        if (focusable.length === 0) {
          // Keep focus on panel
          e.preventDefault();
          panelRef.current?.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement as HTMLElement;
        if (e.shiftKey && (active === first || !container.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (
          !e.shiftKey &&
          (active === last || !container.contains(active))
        ) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [handleKeyDown]);

  const isHorizontal = side === 'left' || side === 'right';

  const translateClosed = () => {
    switch (side) {
      case 'left':
        return '-translate-x-full';
      case 'right':
        return 'translate-x-full';
      case 'top':
        return '-translate-y-full';
      case 'bottom':
        return 'translate-y-full';
      default:
        return 'translate-x-full';
    }
  };

  const positionClass = () => {
    switch (side) {
      case 'left':
        return 'left-0 top-0 h-full';
      case 'right':
        return 'right-0 top-0 h-full';
      case 'top':
        // Cap height so a tall body scrolls internally instead of pushing the
        // header (and its actions, e.g. Save) off-screen.
        return 'top-0 left-0 w-full max-h-[90dvh]';
      case 'bottom':
        // Centre horizontally with auto margins instead of `left-1/2 -translate-x-1/2`.
        // A `-50%` self-width translate lands on a half-pixel whenever the panel
        // width is odd, and because the panel is a transform/transition layer that
        // sub-pixel offset blurs all the text inside it. `inset-x-0 mx-auto` centres
        // on whole pixels, so the slide-in animation only needs a Y translate.
        // `max-h-[90dvh]` keeps the header (Save) visible and scrolls the body.
        return 'bottom-0 inset-x-0 mx-auto w-[95%] md:w-3/4 xl:w-1/2 max-h-[90dvh]';
      default:
        return 'right-0 top-0 h-full';
    }
  };

  // Only apply inline size styles when they're numeric values or specific strings
  // This allows Tailwind classes in className to work for 'fit', 'auto', etc.
  const sizeStyle: React.CSSProperties = {};
  if (isHorizontal) {
    if (typeof width === 'number') {
      sizeStyle.width = `${width}px`;
    } else if (width && width !== 'fit' && width !== 'auto') {
      sizeStyle.width = width;
    }
  } else {
    if (typeof height === 'number') {
      sizeStyle.height = `${height}px`;
    } else if (height && height !== 'fit' && height !== 'auto') {
      sizeStyle.height = height;
    }
  }

  // Build ARIA labelling props without nested ternaries
  const dialogAriaProps: Record<string, string> = {};
  if (ariaLabelledBy) {
    dialogAriaProps['aria-labelledby'] = ariaLabelledBy;
  } else if (ariaLabel) {
    dialogAriaProps['aria-label'] = ariaLabel;
  }

  const onOverlayKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === KEY_BOARD.ENTER.KEY || e.key === KEY_BOARD.SPACE.KEY) {
      e.preventDefault();
      onClose();
    }
  };

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className={`fixed inset-0 ${overlayZIndexClass} ${isOpen ? '' : 'pointer-events-none'}`}
      aria-hidden={!isOpen}
      role='dialog'
      aria-modal='true'
      {...dialogAriaProps}>
      <div
        className={`absolute inset-0 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}
        onClick={closeOnOverlayClick ? onClose : undefined}
        role={closeOnOverlayClick ? 'button' : undefined}
        tabIndex={closeOnOverlayClick ? 0 : -1}
        aria-label={closeOnOverlayClick ? 'Close drawer' : undefined}
        onKeyDown={closeOnOverlayClick ? onOverlayKeyDown : undefined}
      />
      <div
        className={`absolute shadow-xl transition-transform duration-300 ~rounded-t-md/xl ease-in-out flex flex-col bg-primary-50 dark:bg-secondary-dark-200 ${positionClass()} ${
          isOpen ? 'translate-x-0 translate-y-0' : translateClosed()
        } ${className}`}
        style={sizeStyle}
        ref={panelRef}
        tabIndex={TAB_INDEX.SKIP}>
        <div className='flex items-center justify-between border-b ~px-4/6 ~py-2/4 shadow-sm shadow-primary-400 dark:shadow-secondary-dark-300 flex-shrink-0 ~gap-2/3'>
          {typeof header === 'string' ? (
            <h5
              className='~text-sm/base font-semibold truncate min-w-0'
              {...(!ariaLabelledBy ? {} : { id: ariaLabelledBy })}>
              {header}
            </h5>
          ) : (
            <div
              className='~text-sm/base font-semibold min-w-0 flex-1'
              {...(!ariaLabelledBy ? {} : { id: ariaLabelledBy })}>
              {header}
            </div>
          )}

          <div className='flex items-center ~gap-4/6 shrink-0'>
            {headerActions}
            {showCloseButton &&
              (closeButton ?? (
                <BaseButtonSm
                  icon={
                    <CloseCircle
                      styles='~w-4/5 h-auto'
                      fill={COLOR.PRIMARY.LIGHT_900}
                    />
                  }
                  onClick={onClose}
                  variant='secondary'
                />
              ))}
          </div>
        </div>
        <div className='overflow-auto ~p-4/8 flex-1 min-h-0 scrollbar-thin-custom '>
          {children}
        </div>
        {footer && (
          <div className='border-t ~p-4/8 flex-shrink-0'>{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Drawer;
